// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import { PARENT_WIDTH } from '../constants/constants';
import {
  layoutChildren,
  computeGroupDimensions,
  updateSiblings,
  resolveClosedGroupHeight,
} from '../layoutCore/layoutCore';

/* -------------------------------------------------------
   Toggle open / close with cache
------------------------------------------------------- */

export const getAncestorChain = (nodeId, nodes, studioUuid) => {
  const chain = [];

  let current = nodes.find(n => n.id === nodeId);

  while (current?.parentId) {
    const parent = nodes.find(n => n.id === current.parentId);
    if (!parent) {
      break;
    }

    // remove facility from chain if present
    if (parent?.data?.state?.name !== studioUuid) {
      chain.unshift(parent.id);
    }

    current = parent;
  }

  return chain;
};
const setDescendantsHidden = (nodes, parentId, hidden, cache) => {
  if (hidden) {
    // Recursively hide all descendants without changing their isOpen state
    const allChildren = nodes.filter(n => n.parentId === parentId);
    allChildren.forEach(child => {
      nodes = setDescendantsHidden(nodes, child.id, true, cache);
    });
    return nodes.map(n => (n.parentId === parentId ? { ...n, hidden: true } : n));
  }

  // Unhiding: reveal only direct children; open children are restored via toggleNode
  return nodes.map(n => (n.parentId === parentId ? { ...n, hidden: false } : n));
};

const toggleNodeCore = (nodes, parentId, forceOpen = null, cache = new Map()) => {
  let updated = [...nodes];

  const parent = updated.find(n => n.id === parentId);
  if (!parent) {
    return nodes;
  }

  const isOpen = forceOpen !== null ? forceOpen : !parent.data.isOpen;

  /* =====================================================
     Ensure all ancestors are open if opening a node
  ===================================================== */
  if (isOpen) {
    const ancestors = getAncestorChain(parentId, updated);

    ancestors.forEach(ancestorId => {
      const ancestor = updated.find(n => n.id === ancestorId);
      if (!ancestor) {
        return;
      }

      if (!ancestor.data.isOpen) {
        ancestor.data = {
          ...ancestor.data,
          isOpen: true,
        };

        // make its direct children visible
        updated = updated.map(n => (n.parentId === ancestorId ? { ...n, hidden: false } : n));
      }
    });
  }

  /* =====================================================
     Update the toggled node's open state
  ===================================================== */
  updated = updated.map(n =>
    n.id === parentId
      ? {
          ...n,
          data: {
            ...n.data,
            isOpen,
          },
        }
      : n
  );

  /* =====================================================
     Handle children visibility
  ===================================================== */
  updated = setDescendantsHidden(updated, parentId, !isOpen, cache);

  /* =====================================================
     if opening, layout children to calculate their heights for accurate parent height calculation
  ===================================================== */
  if (isOpen) {
    // Restore previously-open children fully (layout + height) before calculating parent height
    updated
      .filter(n => n.parentId === parentId && n.data.isOpen && !n.hidden)
      .forEach(child => {
        updated = toggleNodeCore(updated, child.id, true, cache);
      });

    updated = layoutChildren(updated, parentId);
  }

  /* =====================================================
     Recursively update height of all ancestors and layout siblings to reflect changes
  ===================================================== */
  const fullChain = [...getAncestorChain(parentId, updated), parentId].reverse();

  fullChain.forEach(id => {
    const node = updated.find(n => n.id === id);
    if (!node) {
      return;
    }

    const { height: newHeight, width: newWidth } = node.data.isOpen
      ? computeGroupDimensions(updated, id)
      : {
          height: resolveClosedGroupHeight(node.data?.handles),
          width: node.data?.baseWidth ?? node.data?.width ?? PARENT_WIDTH,
        };

    // update height and width
    updated = updated.map(n =>
      n.id === id
        ? {
            ...n,
            data: {
              ...n.data,
              height: newHeight,
              width: newWidth,
            },
          }
        : n
    );

    // relayout its children
    updated = layoutChildren(updated, id);

    // update siblings stacking
    updated = updateSiblings(updated, id);
  });

  return updated;
};

export const toggleNode = (nodes, parentId, forceOpen = null, cache = new Map()) =>
  toggleNodeCore(nodes, parentId, forceOpen, cache);
